import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Text } from 'office-ui-fabric-react';
import DetailsList from '../DetailsList';
import styles from './AdvanceGroupsList.style';
import { contractStatus, formatDateString, formatNumberWithCommas } from '../../utils';
import StatusField from '../StatusField';

const getColumns = contractCurrency => [
  {
    key: 'status',
    name: 'Status',
    headerClassName: styles.iconColumnHeader,
    iconClassName: styles.headerIcon,
    isIconOnly: true,
    ariaLabel: 'Status',
    iconName: 'Product',
    fieldName: 'status',
    minWidth: 50,
    maxWidth: 100,
    columnActionsMode: 0,
    onRender: (advancegroup, index) => {
      const status = contractStatus(
        advancegroup.contractEffectiveDate,
        advancegroup.contractTerminationDate,
        advancegroup.contractApprovalStatus,
      );

      return <StatusField status={status} index={index} />;
    },
  },
  {
    key: 'name',
    name: 'Advance Group Name',
    fieldName: 'name',
    isResizable: true,
    minWidth: 80,
    maxWidth: 230,
    className: styles.alignLeft,
    onRender(advancegroup) {
      return (
        <Text styles={{ root: { whiteSpace: 'pre-wrap', fontWeight: 400, fontSize: '0.75rem' } }}>
          {advancegroup.name}
        </Text>
      );
    },
  },
  {
    key: 'contractId',
    name: 'Contract Id',
    fieldName: 'contractId',
    isResizable: true,
    maxWidth: 100,
    className: styles.alignLeft,
  },
  {
    key: 'contractName',
    name: 'Contract Name',
    fieldName: 'contractName',
    isResizable: true,
    minWidth: 80,
    maxWidth: 230,
    className: styles.alignLeft,
  },
  {
    key: 'partnerName',
    name: 'Partner Name',
    fieldName: 'partnerName',
    isResizable: true,
    minWidth: 80,
    maxWidth: 230,
    className: styles.alignLeft,
  },
  {
    key: 'balance',
    name: (
      <div style={{ display: 'flex' }}>
        <div className={styles.alignBalanceHeader}>Balance</div>
      </div>
    ),
    fieldName: 'balance',
    isResizable: true,
    minWidth: 100,
    maxWidth: 100,
    className: styles.alignRight,
    onRender: advancegroup => {
      if (advancegroup.calculatedLiveBalance < 0) {
        return (
          <span className={styles.negativeAmount}>
            {`(${formatNumberWithCommas(
              Math.abs(advancegroup.calculatedLiveBalance).toFixed(2),
            )} ${contractCurrency})`}
          </span>
        );
      }
      return `${formatNumberWithCommas(
        advancegroup.calculatedLiveBalance?.toFixed(2) || (0).toFixed(2),
      )} ${contractCurrency}`;
    },
  },
  {
    key: 'createdTimestamp',
    name: (
      <div style={{ display: 'flex' }}>
        <div className={styles.alignDateCreatedHeader}>Date Created</div>
      </div>
    ),
    fieldName: 'createdTimestamp',
    isResizable: true,
    className: styles.alignCenter,
    onRender: advancegroup =>
      advancegroup.createdTimestamp ? `${formatDateString(advancegroup.createdTimestamp)}` : '',
  },
];

const AdvanceGroupsList = ({
  items,
  className: classNameProp,
  contractCurrency,
  disabled,
  hiddenColumns,
  ...other
}) => {
  let columns = getColumns(contractCurrency);
  const className = clsx(styles.root, classNameProp);
  if (hiddenColumns && hiddenColumns.length > 0) {
    columns = columns.filter(column => !hiddenColumns.includes(column.key));
  }

  return (
    <div data-automationid="advancegroup-detail-list">
      <DetailsList
        items={items.map(advancegroups => ({
          ...advancegroups,
          key: advancegroups._id,
          link: `/advancegroups/${advancegroups._id}`,
          dataAutomationId: `row-${advancegroups._id}`,
        }))}
        columns={columns}
        className={className}
        // compactRows is the styling that we apply in our code base which does not handle lots of edge cases
        // and should be deprecated at some point.
        // compact is the api used by fluent ui to make the list compact which is what our styling was trying
        // to achieve
        compactRows={false}
        compact
        {...other}
      />
    </div>
  );
};

AdvanceGroupsList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      type: PropTypes.string,
      contractId: PropTypes.string,
      dataAutomationId: PropTypes.string,
    }),
  ),
  hiddenColumns: PropTypes.arrayOf(PropTypes.string),
  className: PropTypes.string,
  disabled: PropTypes.bool,
  contractCurrency: PropTypes.string,
};

AdvanceGroupsList.defaultProps = {
  items: [],
  hiddenColumns: [],
  className: '',
  disabled: false,
  contractCurrency: '',
};

export default AdvanceGroupsList;
